import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { RegimenForm } from '../../components/Regimens/RegimenForm';
import { RegimenList } from '../../components/Regimens/RegimenList';
import { ActionBar } from '../../components/ActionBar';
import { getRegimens, saveRegimen, deleteRegimen, clearRegimens } from '../../utils/storage/regimens';
import { downloadCSV, parseCSV } from '../../utils/csv';
import { regimens as regimensMapper } from '../../utils/csv/mappers';
import type { Regimen } from '../../types/data';
import { RegimenHero } from './RegimenHero';

export const Regimens: React.FC = () => {
  const [regimens, setRegimens] = useState<Regimen[]>(getRegimens());
  const [showForm, setShowForm] = useState(false);
  const [editingRegimen, setEditingRegimen] = useState<Regimen | undefined>();

  const handleSubmit = (regimen: Regimen) => {
    saveRegimen(regimen);
    setRegimens(getRegimens());
    setShowForm(false);
    setEditingRegimen(undefined);
  };

  const handleEdit = (regimen: Regimen) => {
    setEditingRegimen(regimen);
    setShowForm(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this regimen?')) {
      deleteRegimen(id);
      setRegimens(getRegimens());
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingRegimen(undefined);
  };

  const handleClear = () => {
    clearRegimens();
    setRegimens([]);
  };

  const handleExport = () => {
    const columns = regimensMapper.regimenHeaders.map(header => ({
      header,
      accessor: (item: Regimen) => regimensMapper.toCSV(item)[header]
    }));
    downloadCSV(regimens, columns, 'regimens');
  };

  const handleImport = async (csvData: string) => {
    try {
      const parsed = await parseCSV(csvData);
      const imported = parsed.map(row => regimensMapper.fromCSV(row));
      imported.forEach(item => saveRegimen(item as Regimen));
      setRegimens(getRegimens());
    } catch (error) {
      console.error('Failed to import regimens:', error);
      alert('Failed to import CSV file. Please check the file format.');
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">

      <RegimenHero />

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Regimens</h1>
        <div className="flex gap-4">
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Regimen
          </button>
        </div>
      </div>
      <ActionBar
        onClear={handleClear}
        onExport={handleExport}
        onImport={handleImport}
        onSample={handleImport}
        sampleUrl="https://raw.githubusercontent.com/peptidenow/sample-data/refs/heads/main/regimens.csv"
      />
      {showForm ? (
        <div className="mb-8">
          <RegimenForm
            onSubmit={handleSubmit}
            initialData={editingRegimen}
            onCancel={handleCancel}
          />
        </div>
      ) : (
        <RegimenList
          regimens={regimens}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
  
};
